import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import api from "@/lib/api";
import { InstallPrompt } from "@/components/InstallPrompt";
import { LanguageProvider } from "@/context/LanguageContext";

import { AuthProvider } from "@/context/AuthContext";
import { ClientAuthProvider } from "@/context/ClientAuthContext";
import Layout from "@/components/Layout";
import Home from "@/pages/Home";
import ReferralLanding from "@/pages/ReferralLanding";
import StartHere from "@/pages/StartHere";
import CostCalculator from "@/pages/CostCalculator";
import Contact from "@/pages/Contact";
import Book from "@/pages/Book";
import VideoRoom from "@/pages/VideoRoom";
import BookingConfirmation from "@/pages/BookingConfirmation";
import ManageBooking from "@/pages/ManageBooking";
import ExploreMexico from "@/pages/ExploreMexico";
import ContentPage from "@/pages/ContentPage";
import MeridaPage from "@/pages/MeridaPage";
import NotFound from "@/pages/NotFound";
import Blog from "@/pages/Blog";
import BlogPost from "@/pages/BlogPost";
import Updates from "@/pages/Updates";
import PlanRoute from "@/pages/PlanRoute";
import QuoteAccept from "@/pages/QuoteAccept";
import ClientLogin from "@/pages/ClientLogin";
import ResetPassword from "@/pages/ResetPassword";
import ClientPortal from "@/pages/ClientPortal";
import LifeInMexico from "@/pages/LifeInMexico";
import MovingChecklist from "@/pages/MovingChecklist";
import MeetOurDrivers from "@/pages/MeetOurDrivers";
import RouteGuide from "@/pages/RouteGuide";
import About from "@/pages/About";
import Services from "@/pages/Services";
import Faq from "@/pages/Faq";
import VsPage from "@/pages/VsPage";
import Privacy from "@/pages/Privacy";
import Reviews from "@/pages/Reviews";
import PolicyPage from "@/pages/PolicyPage";
import ReviewForm from "@/pages/ReviewForm";
import References from "@/pages/References";
import VouchForm from "@/pages/VouchForm";
import NicosStory from "@/pages/NicosStory";
import Admin from "@/pages/admin/Admin";
import TripShare from "@/pages/TripShare";
import TrackSim from "@/pages/TrackSim";

const CONTENT_SLUGS = [
  "your-route",
  "residency-documents",
  "fmm-land-entry",
  "driving",
  "drivers-transport",
  "pets",
  "pet-transport",
  "cities-housing",
  "mexico-city",
  "guadalajara",
  "queretaro",
  "puerto-vallarta",
  "san-miguel-de-allende",
  "oaxaca-city",
  "monterrey",
  "cost-planning",
  "arrival-settling",
  "safety-travel-planning",
  "how-we-help",
];

function PublicRoute({ children }) {
  return <Layout>{children}</Layout>;
}

function App() {
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("utm_source") === "referral" && !sessionStorage.getItem("hm_ref_tracked")) {
      sessionStorage.setItem("hm_ref_tracked", "1");
      api.post("/referral/visit", { source: "email", path: window.location.pathname }).catch(() => {});
    }
  }, []);
  return (
    <BrowserRouter>
      <LanguageProvider>
      <AuthProvider>
      <ClientAuthProvider>
        <Toaster position="top-center" richColors />
        <InstallPrompt />
        <Routes>
          <Route path="/" element={<PublicRoute><Home /></PublicRoute>} />
          <Route path="/r/:code" element={<PublicRoute><ReferralLanding /></PublicRoute>} />
          <Route path="/start-here" element={<PublicRoute><StartHere /></PublicRoute>} />
          <Route path="/cost-calculator" element={<PublicRoute><CostCalculator /></PublicRoute>} />
          <Route path="/contact" element={<PublicRoute><Contact /></PublicRoute>} />
          <Route path="/privacy" element={<PublicRoute><Privacy /></PublicRoute>} />
          <Route path="/reviews" element={<PublicRoute><Reviews /></PublicRoute>} />
          <Route path="/review-policy" element={<PublicRoute><PolicyPage slug="review-policy" /></PublicRoute>} />
          <Route path="/photo-policy" element={<PublicRoute><PolicyPage slug="photo-policy" /></PublicRoute>} />
          <Route path="/review/:token" element={<PublicRoute><ReviewForm /></PublicRoute>} />
          <Route path="/references" element={<PublicRoute><References /></PublicRoute>} />
          <Route path="/vouch/:member" element={<PublicRoute><VouchForm /></PublicRoute>} />
          <Route path="/nicos-story" element={<PublicRoute><NicosStory /></PublicRoute>} />
          <Route path="/book" element={<PublicRoute><Book /></PublicRoute>} />
          <Route path="/booking/confirmation" element={<PublicRoute><BookingConfirmation /></PublicRoute>} />
          <Route path="/booking/manage" element={<PublicRoute><ManageBooking /></PublicRoute>} />
          <Route path="/call" element={<PublicRoute><VideoRoom /></PublicRoute>} />
          <Route path="/explore-mexico" element={<PublicRoute><ExploreMexico /></PublicRoute>} />
          <Route path="/merida" element={<PublicRoute><MeridaPage /></PublicRoute>} />
          <Route path="/blog" element={<PublicRoute><Blog /></PublicRoute>} />
          <Route path="/blog/:slug" element={<PublicRoute><BlogPost /></PublicRoute>} />
          <Route path="/updates" element={<PublicRoute><Updates /></PublicRoute>} />
          <Route path="/life-in-mexico" element={<PublicRoute><LifeInMexico /></PublicRoute>} />
          <Route path="/life-in-mexico/:slug" element={<PublicRoute><LifeInMexico /></PublicRoute>} />
          <Route path="/moving-checklist" element={<PublicRoute><MovingChecklist /></PublicRoute>} />
          <Route path="/drivers" element={<PublicRoute><MeetOurDrivers /></PublicRoute>} />
          <Route path="/routes/:slug" element={<PublicRoute><RouteGuide /></PublicRoute>} />
          <Route path="/about" element={<PublicRoute><About /></PublicRoute>} />
          <Route path="/services" element={<PublicRoute><Services /></PublicRoute>} />
          <Route path="/faq" element={<PublicRoute><Faq /></PublicRoute>} />
          <Route path="/vs/:slug" element={<PublicRoute><VsPage /></PublicRoute>} />
          {CONTENT_SLUGS.map((slug) => (
            <Route key={slug} path={`/${slug}`} element={<PublicRoute><ContentPage slug={slug} /></PublicRoute>} />
          ))}
          <Route path="/admin" element={<Admin />} />
          <Route path="/plan/:token" element={<PlanRoute />} />
          <Route path="/quote/:token" element={<QuoteAccept />} />
          <Route path="/account" element={<ClientLogin />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/portal" element={<ClientPortal />} />
          <Route path="/track/:shareToken" element={<TripShare />} />
          <Route path="/track-sim" element={<TrackSim />} />
          <Route path="*" element={<PublicRoute><NotFound /></PublicRoute>} />
        </Routes>
      </ClientAuthProvider>
      </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
